import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// DB types matching the challenges and challenge_solutions tables
export interface DbChallenge {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  target_audience: string | null;
  kpi_name: string | null;
  kpi_base_value: string | null;
  kpi_expected_value: string | null;
  status: 'draft' | 'active' | 'completed';
  created_at: string;
}

export interface DbChallengeSolution {
  id: string;
  challenge_id: string;
  name: string;
  solution_type: string | null;
  description: string | null;
  data_used: string | null;
  suggested_tools: string[] | null;
  converted_to_project: boolean | null;
  created_at: string;
}

export const SOLUTION_TYPES = [
  { id: 'content', name: 'Creación de Contenido', icon: '✍️', description: 'Generación de textos, imágenes, videos con IA' },
  { id: 'data', name: 'Análisis de Datos', icon: '📊', description: 'Procesamiento y visualización de información' },
  { id: 'assistant', name: 'Asistentes IA', icon: '🤖', description: 'Chatbots y agentes conversacionales' },
  { id: 'automation', name: 'Automatizaciones', icon: '⚡', description: 'Flujos de trabajo automatizados' },
  { id: 'coding', name: 'Vibe Coding', icon: '💻', description: 'Desarrollo de aplicaciones con IA' },
];

export const SUGGESTED_TOOLS: Record<string, string[]> = {
  content: ['ChatGPT', 'Claude', 'Midjourney', 'DALL-E', 'Canva AI'],
  data: ['Power BI', 'Tableau', 'Python + Pandas', 'Google Sheets + AI'],
  assistant: ['ChatGPT API', 'Dialogflow', 'Botpress', 'Voiceflow'],
  automation: ['Zapier', 'Make', 'Power Automate', 'n8n'],
  coding: ['Lovable', 'Cursor', 'GitHub Copilot', 'Replit'],
};

export function useChallenges() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['challenges', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('challenges')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as DbChallenge[];
    },
    enabled: !!user,
  });
}

export function useChallengeSolutions(challengeId: string | null) {
  return useQuery({
    queryKey: ['challenge_solutions', challengeId],
    queryFn: async () => {
      if (!challengeId) return [];
      const { data, error } = await supabase
        .from('challenge_solutions')
        .select('*')
        .eq('challenge_id', challengeId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as DbChallengeSolution[];
    },
    enabled: !!challengeId,
  });
}

export function useAllSolutions() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['all_challenge_solutions', user?.id],
    queryFn: async () => {
      if (!user) return [];
      // Get all challenges for this user first
      const { data: challenges } = await supabase
        .from('challenges')
        .select('id')
        .eq('user_id', user.id);
      if (!challenges || challenges.length === 0) return [];
      
      const challengeIds = challenges.map(c => c.id);
      const { data, error } = await supabase
        .from('challenge_solutions')
        .select('*')
        .in('challenge_id', challengeIds)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as DbChallengeSolution[];
    },
    enabled: !!user,
  });
}

export function useCreateChallenge() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { title: string; description?: string; target_audience?: string; kpi_name?: string; kpi_base_value?: string; kpi_expected_value?: string; status?: string }) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('challenges')
        .insert({
          user_id: user.id,
          title: input.title || 'Nuevo reto',
          description: input.description || null,
          target_audience: input.target_audience || null,
          kpi_name: input.kpi_name || null,
          kpi_base_value: input.kpi_base_value || null,
          kpi_expected_value: input.kpi_expected_value || null,
          status: (input.status as any) || 'draft',
        })
        .select()
        .single();
      if (error) throw error;
      return data as DbChallenge;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['challenges'] });
    },
  });
}

export function useUpdateChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<Pick<DbChallenge, 'title' | 'description' | 'target_audience' | 'kpi_name' | 'kpi_base_value' | 'kpi_expected_value' | 'status'>>) => {
      const { error } = await supabase
        .from('challenges')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['challenges'] });
    },
  });
}

export function useDeleteChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('challenges').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['challenges'] });
      qc.invalidateQueries({ queryKey: ['challenge_solutions'] });
      qc.invalidateQueries({ queryKey: ['all_challenge_solutions'] });
    },
  });
}

export function useCreateSolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { challenge_id: string; name: string; solution_type?: string; description?: string; data_used?: string; suggested_tools?: string[]; converted_to_project?: boolean }) => {
      const { data, error } = await supabase
        .from('challenge_solutions')
        .insert({
          challenge_id: input.challenge_id,
          name: input.name,
          solution_type: input.solution_type || null,
          description: input.description || null,
          data_used: input.data_used || null,
          suggested_tools: input.suggested_tools || [],
          converted_to_project: input.converted_to_project || false,
        })
        .select()
        .single();
      if (error) throw error;

      // Update challenge status
      await supabase
        .from('challenges')
        .update({ status: 'active' })
        .eq('id', input.challenge_id);

      return data as DbChallengeSolution;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['challenge_solutions'] });
      qc.invalidateQueries({ queryKey: ['all_challenge_solutions'] });
      qc.invalidateQueries({ queryKey: ['challenges'] });
    },
  });
}

export function useChallengeSession(challengeId: string | null) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ['challenge_session', challengeId],
    queryFn: async () => {
      if (!challengeId || !user) return null;
      const { data, error } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('context_type', 'challenge')
        .eq('context_id', challengeId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!challengeId && !!user,
  });

  const { data: chatMessages = [], isLoading: messagesLoading } = useQuery({
    queryKey: ['challenge_session_messages', session?.id],
    queryFn: async () => {
      if (!session) return [];
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', session.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!session?.id,
  });

  const createSession = async (title: string): Promise<string> => {
    if (!user || !challengeId) throw new Error('Missing user or challenge');
    const { data, error } = await supabase
      .from('chat_sessions')
      .insert({
        user_id: user.id,
        context_type: 'challenge',
        context_id: challengeId,
        title: title.slice(0, 100),
      })
      .select()
      .single();
    if (error) throw error;
    qc.invalidateQueries({ queryKey: ['challenge_session', challengeId] });
    return data.id;
  };

  const addMessage = async (sessionId: string, role: 'user' | 'assistant', content: string) => {
    const { error } = await supabase
      .from('chat_messages')
      .insert({
        session_id: sessionId,
        role,
        content,
      });
    if (error) throw error;
    qc.invalidateQueries({ queryKey: ['challenge_session_messages', sessionId] });
  };

  return {
    session,
    chatMessages,
    isLoading: sessionLoading || messagesLoading,
    createSession,
    addMessage,
  };
}

// Convención canónica (ver SOLUTION_TYPE_MAP en useProjects.ts):
// 1 Contenido · 2 Vibecoding · 3 Asistente · 4 Automatización · 5 Análisis de Datos
const SOLUTION_TYPE_MAP: Record<string, number> = {
  content: 1,
  coding: 2,
  assistant: 3,
  automation: 4,
  data: 5,
};

export function useConvertToProject() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ solution, challenge, isFreeUser }: { solution: DbChallengeSolution; challenge: DbChallenge; isFreeUser?: boolean }) => {
      if (!user) throw new Error('Not authenticated');

      // Check project limit for free users
      if (isFreeUser) {
        const { count, error: countError } = await supabase
          .from('participant_projects')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);
        if (countError) throw countError;
        if ((count ?? 0) >= 1) {
          throw new Error('FREE_LIMIT_REACHED');
        }
      }

      // Get active cohort for the user
      const { data: enrollment } = await supabase
        .from('cohort_participants')
        .select('cohort_id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      const solutionTypeNum = solution.solution_type
        ? SOLUTION_TYPE_MAP[solution.solution_type] ?? null
        : null;

      // 1a. Encontrar la chat_session que originó este challenge (conexión 1-a-1)
      const { data: originSession } = await supabase
        .from('chat_sessions')
        .select('id')
        .eq('user_id', user.id)
        .eq('context_type', 'challenge')
        .eq('context_id', challenge.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // 1b. Create project (con source_session_id si existe la conversación)
      const { data: project, error: projError } = await supabase
        .from('participant_projects')
        .insert({
          user_id: user.id,
          cohort_id: enrollment?.cohort_id ?? null,
          name: solution.name,
          problem: challenge.description || null,
          solution_description: solution.description || null,
          solution_type: solutionTypeNum,
          kpi_name: challenge.kpi_name || null,
          kpi_current_value: challenge.kpi_base_value || null,
          kpi_target_value: challenge.kpi_expected_value || null,
          target_audience: challenge.target_audience || null,
          data_used: solution.data_used || null,
          status: 'ideation',
          source_session_id: originSession?.id ?? null,
        })
        .select()
        .single();
      if (projError) throw projError;

      // 2. Mark solution as converted
      await supabase
        .from('challenge_solutions')
        .update({ converted_to_project: true })
        .eq('id', solution.id);

      // 3. Sync tools
      if (solution.suggested_tools && solution.suggested_tools.length > 0) {
        await supabase.from('project_tools').insert(
          solution.suggested_tools.map(t => ({ project_id: project.id, tool_name: t }))
        );
      }

      return project;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['challenge_solutions'] });
      qc.invalidateQueries({ queryKey: ['all_challenge_solutions'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
